<script setup lang="ts">
import type { ThemeSettings, TextSettings } from '~/stores/auth'
import { DEFAULT_THEME_SETTINGS } from '~/stores/auth'
import type { AudioPlayerState } from '~/composables/useAudioPlayer'

const props = defineProps<{
  showPlayer: boolean
  themeSettings?: ThemeSettings
  textSettings?: TextSettings
}>()

// Inject shared audio state from CharacterPageShell
const ap = inject<AudioPlayerState>('audioPlayer')!

function hexToRgb(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return [r, g, b]
}

const playerCardStyle = computed(() => {
  const theme = props.themeSettings ?? DEFAULT_THEME_SETTINGS
  const rgb = hexToRgb(theme.cardColor)
  return {
    background: `rgba(${rgb.join(',')}, ${theme.cardOpacity / 100})`,
    backdropFilter: theme.cardBlur > 0 ? `blur(${theme.cardBlur}px)` : undefined,
    WebkitBackdropFilter: theme.cardBlur > 0 ? `blur(${theme.cardBlur}px)` : undefined
  }
})

const volumeSliderStyle = computed(() => {
  const pct = Math.round(ap.volume.value * 100)
  const theme = props.themeSettings ?? DEFAULT_THEME_SETTINGS
  const rgb = hexToRgb(theme.primaryColor)
  return {
    background: `linear-gradient(to right, rgba(${rgb.join(',')}, 0.6) 0%, rgba(${rgb.join(',')}, 0.6) ${pct}%, rgba(255, 255, 255, 0.15) ${pct}%, rgba(255, 255, 255, 0.15) 100%)`
  }
})
</script>

<template>
  <!-- Desktop player - WidgetCorner handles positioning and desktop visibility -->
  <div>
    <!-- Full player -->
    <div
      v-if="showPlayer"
      class="w-72 rounded-2xl border border-white/8 shadow-[0_0_20px_rgba(0,0,0,0.3)]"
      :style="playerCardStyle"
    >
      <!-- Track info -->
      <div class="flex items-center gap-2.5 px-4 pt-3 pb-1.5">
        <div
          class="flex items-end gap-[3px] h-4 shrink-0"
          :class="ap.isPlaying.value ? 'text-primary-400' : 'text-white/20'"
        >
          <div
            v-for="i in 4"
            :key="i"
            class="w-[3px] h-[3px] rounded-full bg-current"
            :class="{ 'wave-bar': ap.isPlaying.value }"
            :style="ap.isPlaying.value ? { animationDelay: `${(i - 1) * 0.15}s` } : undefined"
          />
        </div>
        <p
          class="text-xs text-white/60 truncate flex-1"
          :style="textSettings ? { fontFamily: `${textSettings.fontFamily}, sans-serif`, color: `color-mix(in srgb, ${textSettings.color} 60%, transparent)` } : {}"
        >
          {{ ap.currentTrack.value?.original_name }}
        </p>
      </div>

      <!-- Progress bar -->
      <div class="px-4 py-1">
        <div class="flex items-center gap-2">
          <span class="text-[10px] text-white/30 tabular-nums w-7 text-right">{{ ap.formatTime(ap.currentTime.value) }}</span>
          <div
            class="relative flex-1 h-1 bg-white/10 rounded-full cursor-pointer"
            @click="(e: MouseEvent) => { const el = e.currentTarget as HTMLElement; const rect = el.getBoundingClientRect(); ap.seek(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))) }"
          >
            <div
              class="absolute inset-y-0 left-0 bg-primary-400/60 rounded-full"
              :style="{ width: `${ap.progress.value}%` }"
            />
          </div>
          <span class="text-[10px] text-white/30 tabular-nums w-7">{{ ap.formatTime(ap.duration.value) }}</span>
        </div>
      </div>

      <!-- Controls + Volume -->
      <div class="flex items-center px-3 pb-3 pt-1">
        <div class="flex items-center gap-0.5">
          <button
            v-if="ap.hasMultipleTracks.value"
            class="size-7 flex items-center justify-center text-white/40 hover:text-white/70 transition-colors cursor-pointer"
            @click="ap.prev"
          >
            <UIcon
              name="i-lucide-skip-back"
              class="size-3.5"
            />
          </button>

          <button
            class="size-9 flex items-center justify-center rounded-full bg-primary-500/20 border border-primary-400/30 text-primary-300 hover:bg-primary-500/30 transition-all cursor-pointer"
            @click="ap.togglePlay"
          >
            <UIcon
              :name="ap.isPlaying.value ? 'i-lucide-pause' : 'i-lucide-play'"
              class="size-4"
            />
          </button>

          <button
            v-if="ap.hasMultipleTracks.value"
            class="size-7 flex items-center justify-center text-white/40 hover:text-white/70 transition-colors cursor-pointer"
            @click="ap.next"
          >
            <UIcon
              name="i-lucide-skip-forward"
              class="size-3.5"
            />
          </button>
        </div>

        <div class="flex-1" />

        <div class="flex items-center gap-1.5">
          <button
            class="size-6 flex items-center justify-center text-white/40 hover:text-white/70 transition-colors cursor-pointer"
            @click="ap.toggleMute"
          >
            <UIcon
              :name="ap.volumeIcon.value"
              class="size-3"
            />
          </button>
          <input
            type="range"
            min="0"
            max="100"
            :value="Math.round(ap.volume.value * 100)"
            class="volume-slider"
            :style="volumeSliderStyle"
            @input="(e: Event) => ap.setVolume(Number((e.target as HTMLInputElement).value) / 100)"
          >
        </div>
      </div>
    </div>

    <!-- Minimal player (when showPlayer is disabled) -->
    <div
      v-else
      class="rounded-full border border-white/8 shadow-[0_0_15px_rgba(0,0,0,0.2)]"
      :style="playerCardStyle"
    >
      <div class="flex items-center gap-1 px-2 py-1.5">
        <button
          class="size-7 flex items-center justify-center rounded-full bg-primary-500/20 border border-primary-400/30 text-primary-300 hover:bg-primary-500/30 transition-all cursor-pointer"
          @click="ap.togglePlay"
        >
          <UIcon
            :name="ap.isPlaying.value ? 'i-lucide-pause' : 'i-lucide-play'"
            class="size-3.5"
          />
        </button>

        <div class="w-px h-4 bg-white/10 mx-0.5" />

        <button
          class="size-6 flex items-center justify-center text-white/40 hover:text-white/70 transition-colors cursor-pointer"
          @click="ap.toggleMute"
        >
          <UIcon
            :name="ap.volumeIcon.value"
            class="size-3"
          />
        </button>

        <input
          type="range"
          min="0"
          max="100"
          :value="Math.round(ap.volume.value * 100)"
          class="volume-slider"
          :style="volumeSliderStyle"
          @input="(e: Event) => ap.setVolume(Number((e.target as HTMLInputElement).value) / 100)"
        >
      </div>
    </div>
  </div>
</template>

<style scoped>
.wave-bar {
  animation: wave 1.2s ease-in-out infinite;
}

@keyframes wave {
  0%, 100% { height: 3px; }
  50% { height: 14px; }
}

.volume-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 60px;
  height: 3px;
  border-radius: 2px;
  outline: none;
  cursor: pointer;
}

.volume-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.7);
  cursor: pointer;
}

.volume-slider::-moz-range-thumb {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.7);
  border: none;
  cursor: pointer;
}

.volume-slider::-moz-range-track {
  background: transparent;
  height: 3px;
  border-radius: 2px;
}
</style>
